// src/components/Login.js
import React, { useState, useEffect } from "react";
import supabase from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { UserIcon } from "@heroicons/react/24/outline";
import LoadingModal from "./LoadingModal";
import Logo from "../images/HouvalDark.png"; // Adjust the path as per your project structure
import loginBG from '../images/loginBG.jpg'; // Import the background image

const Login = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState("login"); // 'login', 'signup', 'forgotPassword'
  const [email, setEmail] = useState("");
  const [name, setName] = useState(""); // For sign-up
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // For sign-up
  const [error, setError] = useState("");
  const [message, setMessage] = useState(""); // For success messages

  // Optional: Redirect to /portal if already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/portal");
      }
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        navigate("/portal");
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!email) {
      setError("Please enter your email.");
      return;
    }

    if (view === "forgotPassword") {
      // Handle forgot password
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '/reset-password', // Adjust the redirect URL
        });

        if (error) {
          let errorMessage = error.message || "An unexpected error occurred.";
          setError(errorMessage);
        } else {
          setMessage("Password reset email sent. Please check your inbox.");
        }
      } catch (err) {
        setError("An unexpected error occurred.");
        console.error("Forgot Password Error:", err);
      }
      setIsLoading(false);
    } else if (view === "signup") {
      // Sign-up logic
      if (!name) {
        setError("Please enter your name.");
        return;
      }
      if (!password) {
        setError("Please enter a password.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }

      setIsLoading(true);
      try {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: name
            },
            emailRedirectTo: window.location.origin + '/confirm-email', // Adjust the redirect URL
          }
        });

        if (error) {
          let errorMessage = error.message || "An unexpected error occurred.";
          setError(errorMessage);
        } else {
          // Prompt the user to confirm their email
          setMessage("A confirmation email has been sent. Please check your inbox.");
          // Optionally, switch to login view
          setView('login');
        }
      } catch (err) {
        setError("An unexpected error occurred.");
        console.error("Sign Up Error:", err);
      }
      setIsLoading(false);
    } else {
      // Login logic
      if (!password) {
        setError("Please enter your password.");
        return;
      }
      setIsLoading(true);
      try {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          let errorMessage = "Error in Login";
          switch (error.code) {
            case "AuthApiError":
              errorMessage = "Invalid email or password.";
              break;
            case "PasswordAuth":
              errorMessage = "Incorrect password.";
              break;
            case "UserNotConfirmed":
              errorMessage = "Please confirm your email address before logging in.";
              break;
            default:
              errorMessage = error.message || "An unexpected error occurred.";
              break;
          }
          setError(errorMessage);
        } else {
          navigate("/portal");
        }
      } catch (err) {
        setError("An unexpected error occurred.");
        console.error("Login Error:", err);
      }
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="min-h-screen text-white relative"
        style={{
          backgroundImage: `url(${loginBG})`, // Use the imported image
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Loading Modal */}
        <LoadingModal loadingModalVisible={isLoading} />

        {/* Overlay */}
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 z-0">
          {/* Centered Form Container */}
          <div className="min-h-screen flex items-center justify-center z-10 px-4">
            <div
              className="flex flex-col justify-center items-center bg-gray-50 px-8 py-6 rounded-md shadow-lg w-full max-w-md"
              style={{ borderRadius: "8px" }}
            >
              {/* Logo */}
              <div className="text-center mb-6">
                <img
                  className="mx-auto"
                  src={Logo}
                  alt="Logo"
                  width="150"
                />
              </div>

              {/* Login/Sign-Up/Forgot Password Form */}
              <form onSubmit={handleSubmit} autoComplete="off" className="w-full">
                {/* Name Field (Sign Up only) */}
                {view === "signup" && (
                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="block text-gray-700 text-sm font-bold mb-2"
                    >
                      Name
                    </label>
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                      id="name"
                      type="text"
                      placeholder="Enter your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* Email Field */}
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-gray-700 text-sm font-bold mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                    id="email"
                    type="email"
                    placeholder="Enter Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                {/* Password Field */}
                {(view === "login" || view === "signup") && (
                  <div className="mb-6">
                    <label
                      htmlFor="password"
                      className="block text-gray-700 text-sm font-bold mb-2"
                    >
                      Password
                    </label>
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                      id="password"
                      type="password"
                      placeholder="Enter Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* Confirm Password Field (Sign Up only) */}
                {view === "signup" && (
                  <div className="mb-6">
                    <label
                      htmlFor="confirmPassword"
                      className="block text-gray-700 text-sm font-bold mb-2"
                    >
                      Confirm Password
                    </label>
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm Password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* Submit Button */}
                <div className="mb-4">
                  <button
                    className="bg-yellow-400 text-black font-bold py-2 px-4 w-full flex items-center justify-center rounded hover:bg-yellow-600 focus:outline-none focus:ring focus:border-yellow-300 transition duration-200"
                    type="submit"
                  >
                    <UserIcon className="h-5 w-5 mr-2" /> {view === "signup" ? 'Sign Up' : view === "forgotPassword" ? 'Reset Password' : 'Login'}
                  </button>
                </div>

                {/* Error Message */}
                {error && (
                  <p className="text-red-500 text-xs italic text-center mb-4">
                    {error}
                  </p>
                )}

                {/* Success Message */}
                {message && (
                  <p className="text-green-500 text-xs italic text-center mb-4">
                    {message}
                  </p>
                )}

                {/* Links to toggle between views */}
                <div className="text-center mt-4">
                  {view === "login" && (
                    <>
                      <button
                        type="button"
                        className="inline-block align-baseline font-bold text-sm text-gray-700"
                        onClick={() => setView('forgotPassword')}
                      >
                        Forgot Password?
                      </button>
                      <p className="text-gray-700 mt-2">
                        Don't have an account?{' '}
                        <button
                          type="button"
                          className="inline-block align-baseline font-bold text-sm text-gray-700"
                          onClick={() => setView('signup')}
                        >
                          Create account
                        </button>
                      </p>
                    </>
                  )}
                  {view === "signup" && (
                    <p className="text-gray-700">
                      Already have an account?{' '}
                      <button
                        type="button"
                        className="inline-block align-baseline font-bold text-sm text-gray-700"
                        onClick={() => setView('login')}
                      >
                        Log in to existing account
                      </button>
                    </p>
                  )}
                  {view === "forgotPassword" && (
                    <p className="text-gray-700">
                      Remembered your password?{' '}
                      <button
                        type="button"
                        className="inline-block align-baseline font-bold text-sm text-gray-700"
                        onClick={() => setView('login')}
                      >
                        Log in to existing account
                      </button>
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive Styles */}
      <style jsx>{`
        @media (min-width: 769px) {
          .flex.flex-col {
            width: 400px; /* Fixed width for non-mobile devices */
          }
        }
        @media (max-width: 768px) {
          .flex.flex-col {
            width: 100%; /* Ensure full width on mobile devices */
          }
        }
      `}</style>
    </>
  );
};

export default Login;
